import { DroppableZone } from "@/lib/dnd/components/DroppableZone";
import { useQuestionHandlers } from "@/lib/dnd/hooks/useQuestionHandlers";
import { useLocalImageUpload } from "@/lib/hooks/slides/editor/useLocalImageUpload";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateElement, updateElementProps } from "@/store/slideEditorSlice";
import {
  CANVAS_DEFAULT_FONT_FAMILY,
  answerColors,
  getFillBlanksStyles,
} from "@ai-teach/canvas-styles";
import type { FillBlanksElement } from "@ai-teach/types";
import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { AiFillPicture } from "react-icons/ai";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { PiPaperPlaneTiltFill } from "react-icons/pi";
import { DeleteImageButton } from "./DeleteImageButton";
import { gradedFillStyle } from "./gradedStyles";

/** A blank in the sentence. Outside the editor it also selects that blank, so a reader can step
 *  through the answers the same way the student stepped through the questions. */
function BlankSlot({
  style,
  isLiveMode,
  onSelect,
  children,
}: {
  style: CSSProperties;
  isLiveMode: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  if (!isLiveMode) return <span style={style}>{children}</span>;

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      style={{ border: "none", background: "none", padding: 0, cursor: "pointer", ...style }}
    >
      {children}
    </button>
  );
}

interface FillBlanksRendererProps {
  element: FillBlanksElement;
  isLiveMode?: boolean;
  /** blankIndex → the option index (choice mode) or the text (type mode) the student answered. */
  selectedAnswers?: Record<string, string> | undefined;
  frozenBlanks?: Set<string> | undefined;
  /** The question is over: the correct option turns green even on a blank left empty. */
  isFrozen?: boolean | undefined;
  /** Canvas the element is drawn on, for a render outside the editor's own canvas. */
  canvasHeight?: number | undefined;
}

export function FillBlanksRenderer({
  element,
  isLiveMode = false,
  selectedAnswers,
  frozenBlanks,
  isFrozen,
  canvasHeight,
}: FillBlanksRendererProps) {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isEditingText, setIsEditingText] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const editorCanvasHeight = useAppSelector((state) => state.slideEditor.present.canvasSize.height);
  const fillBlanksStyles = getFillBlanksStyles(canvasHeight ?? editorCanvasHeight);
  const { handleQuestionDragAndDrop } = useQuestionHandlers();
  const { handleFileDrop } = useLocalImageUpload((media, containerId) =>
    handleQuestionDragAndDrop(media, containerId)
  );

  const editableRef = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isEditingText && editableRef.current) {
      editableRef.current.focus();
      // Move cursor to end
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(editableRef.current);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }, [isEditingText]);

  // Editor-only: grow (never shrink) the element's persisted height so a wrapped multi-line
  // question — or a question plus enough answer options that they no longer fit — is never
  // clipped by `outerDiv`'s `overflow: hidden`. Measures the actual rendered content rather than
  // a closed-form formula (unlike Spelling's box math) because this element's content height
  // depends on the question's wrapped line count, the answer mode, and however many
  // options/blocks are configured — all DOM-driven, not a single derivable number. Never runs in
  // live-mode preview, which only renders whatever height is already persisted.
  // biome-ignore lint/correctness/useExhaustiveDependencies: element.text/element.blocks aren't read directly, but re-measuring the ref's DOM size when either changes is the point
  useLayoutEffect(() => {
    if (isLiveMode || !outerRef.current) return;
    const requiredHeight = outerRef.current.scrollHeight;
    if (requiredHeight > element.size.height) {
      dispatch(
        updateElement({
          id: element.id,
          size: { width: element.size.width, height: requiredHeight },
        })
      );
    }
  }, [
    isLiveMode,
    element.id,
    element.size.width,
    element.size.height,
    element.text,
    element.blocks,
    dispatch,
  ]);

  // Backward-compat safety net, runs in EVERY mode (unlike the auto-grow effect above, which only
  // persists a fix in the editor): a slide saved before this element could grow itself — or one
  // whose height was never re-synced because nobody has reopened it in the editor since — would
  // otherwise have its overflow content silently clipped by `outerDiv`'s `overflow: hidden`. This
  // only flips the container to `visible` when content is ACTUALLY taller than the frame, so a
  // properly-sized element is completely unaffected.
  const [contentOverflowsFrame, setContentOverflowsFrame] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measures the ref's DOM size whenever these change, none read directly in the body
  useLayoutEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    setContentOverflowsFrame(el.scrollHeight > el.clientHeight + 1);
  }, [element.size.width, element.size.height, element.text, element.blocks]);

  const safeIndex = Math.max(0, Math.min(currentIndex, (element.blocks?.length ?? 1) - 1));
  const isBlankFrozen = frozenBlanks?.has(`${element.id}-${safeIndex}`) ?? false;
  const typedAnswer = selectedAnswers?.[`${safeIndex}`];
  const correctTypedText = element.blocks?.[safeIndex]?.options[0]?.text ?? "";
  const typedGrade =
    typedAnswer === undefined ? undefined : typedAnswer === correctTypedText ? "correct" : "wrong";
  const showTypedCorrectText =
    typedGrade === "wrong" || (!!isFrozen && typedAnswer === undefined && !!selectedAnswers);

  const handleToggleCorrectAnswer = (blockIndex: number, optionIndex: number) => {
    if (!element.blocks) return;

    const currentBlock = element.blocks[blockIndex];
    if (!currentBlock) return;

    const updatedOptions = currentBlock.options.map((option, i) => ({
      ...option,
      isCorrectAnswer: i === optionIndex,
    }));

    const updatedBlocks = element.blocks.map((block, i) =>
      i === blockIndex ? { ...block, options: updatedOptions } : block
    );

    dispatch(
      updateElementProps({
        id: element.id,
        props: { blocks: updatedBlocks },
      })
    );
  };

  const handleQuestionTextChange = (text: string) => {
    const finalBlankCount = (text.match(/\[\[\]\]/g) || []).length;
    let updatedBlocks = [...(element.blocks || [])];

    if (finalBlankCount < updatedBlocks.length) {
      // remove extra blocks
      updatedBlocks = updatedBlocks.slice(0, finalBlankCount);
    } else if (finalBlankCount > updatedBlocks.length) {
      // add new blocks until the count matches
      while (updatedBlocks.length < finalBlankCount) {
        updatedBlocks.push({
          options: [
            { text: "Answer 1", isCorrectAnswer: true },
            { text: "Answer 2", isCorrectAnswer: false },
            { text: "Answer 3", isCorrectAnswer: false },
          ],
        });
      }
    }

    dispatch(
      updateElementProps({
        id: element.id,
        props: {
          text: text,
          blocks: updatedBlocks,
        },
      })
    );
  };

  const handleAnswerTextChange = (blockIndex: number, optionIndex: number, text: string) => {
    if (!element.blocks) return;

    const currentBlock = element.blocks[blockIndex];
    if (!currentBlock) return;

    const updatedOptions = currentBlock.options.map((option, i) =>
      i === optionIndex
        ? {
            ...option,
            text,
          }
        : option
    );

    const updatedBlocks = element.blocks.map((block, i) =>
      i === blockIndex ? { ...block, options: updatedOptions } : block
    );

    dispatch(
      updateElementProps({
        id: element.id,
        props: { blocks: updatedBlocks },
      })
    );
  };

  /** What the student put in one blank, in either answer mode, and whether it was right. */
  const answeredBlank = (blankIndex: number) => {
    const answered = selectedAnswers?.[`${blankIndex}`];
    if (answered === undefined) return null;
    const options = element.blocks?.[blankIndex]?.options ?? [];
    if (element.answerMode === "type") {
      return { text: answered, isCorrect: answered === (options[0]?.text ?? "") };
    }
    const option = options[Number(answered)];
    return { text: option?.text ?? "", isCorrect: option?.isCorrectAnswer === true };
  };

  const renderTextWithBlanks = () => {
    const parts = element.text.split(/(\[\[\]\]|\n)/g);
    let blankCounter = 0;

    return parts.map((part, index) => {
      if (part === "\n" && !element.isTextOnly) {
        // Image-mode's flex row only wraps on width overflow, not on "\n" — force the break explicitly.
        return (
          <span
            key={`break-${index + 1}`}
            style={{ flexBasis: "100%", width: 0 }}
            aria-hidden="true"
          />
        );
      }

      if (part === "[[]]") {
        const currentBlankIndex = blankCounter;
        blankCounter++;
        const block = element.blocks?.[currentBlankIndex];

        // editor only has two states: currently selected / unselected (no answer state)
        const isCurrentlySelected = currentBlankIndex === safeIndex;
        const underlineColor = isCurrentlySelected ? "rgb(99, 102, 241)" : "#cbd5e1";
        const underlineWrapperStyle = {
          display: "inline-block",
          borderBottom: `2px solid ${underlineColor}`,
          paddingBottom: 2,
          verticalAlign: "middle",
        } as const;

        // Show numbered circle when isTextOnly (both edit and live mode)
        if (element.isTextOnly) {
          const filled = answeredBlank(currentBlankIndex);
          return (
            <BlankSlot
              key={`blank-${index + 1}`}
              style={underlineWrapperStyle}
              isLiveMode={isLiveMode}
              onSelect={() => setCurrentIndex(currentBlankIndex)}
            >
              <span
                style={{
                  ...fillBlanksStyles.textBlankCircle,
                  fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
                }}
              >
                {currentBlankIndex + 1}
              </span>
              {filled && (
                <span
                  style={{
                    fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
                    color: filled.isCorrect ? answerColors.correct : answerColors.wrong,
                    fontWeight: 700,
                    paddingLeft: 4,
                  }}
                >
                  {filled.text}
                </span>
              )}
            </BlankSlot>
          );
        }

        // Show static image or placeholder in live mode when not text-only
        if (isLiveMode) {
          return (
            <BlankSlot
              key={`blank-${index + 1}`}
              style={underlineWrapperStyle}
              isLiveMode={isLiveMode}
              onSelect={() => setCurrentIndex(currentBlankIndex)}
            >
              <span style={fillBlanksStyles.imageContainer}>
                {block?.media?.src ? (
                  <img
                    src={block.media.src}
                    alt={block.media.filename ?? `image-${currentBlankIndex}`}
                    style={fillBlanksStyles.image}
                  />
                ) : (
                  <div className="flex flex-col items-center">
                    <AiFillPicture />
                    <span
                      className="text-[8px] text-slate-500"
                      style={{ fontFamily: CANVAS_DEFAULT_FONT_FAMILY }}
                    >
                      No image
                    </span>
                  </div>
                )}

                <span
                  style={{
                    ...fillBlanksStyles.textBlankCircle,
                    fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
                    position: "absolute",
                    top: 2,
                    left: 2,
                    zIndex: 10,
                  }}
                >
                  {currentBlankIndex + 1}
                </span>
              </span>
            </BlankSlot>
          );
        }

        const handleDeleteImage = (blockIndex: number) => (event: React.MouseEvent) => {
          if (!element.blocks) return;
          event.stopPropagation();

          const updatedBlocks = element.blocks.map((block, index) =>
            index === blockIndex ? { ...block, media: undefined } : block
          );

          dispatch(
            updateElementProps({
              id: element.id,
              props: {
                blocks: updatedBlocks,
              },
            })
          );
        };

        // Show droppable zone when not text-only and in edit mode
        return (
          <span key={`blank-${index + 1}`} style={underlineWrapperStyle}>
            <DroppableZone
              id={`fillBlanksImage-${currentBlankIndex}`}
              data={{
                id: `question//fillBlanksImage//${element.id}//${currentBlankIndex}`,
                type: "fillBlanksImage",
              }}
              allowedType={["image"]}
              style={fillBlanksStyles.imageContainer}
              onLocalFileDrop={(file) =>
                handleFileDrop(
                  file,
                  `question//fillBlanksImage//${element.id}//${currentBlankIndex}`
                )
              }
            >
              <span
                style={{
                  ...fillBlanksStyles.textBlankCircle,
                  position: "absolute",
                  top: 2,
                  left: 2,
                  zIndex: 10,
                }}
              >
                {currentBlankIndex + 1}
              </span>
              {block?.media?.src ? (
                <div className="relative w-full h-full group/image overflow-hidden rounded-lg">
                  <img
                    src={block.media.src}
                    alt={block.media.filename ?? `image-${currentBlankIndex}`}
                    style={fillBlanksStyles.image}
                  />
                  <DeleteImageButton
                    onClick={handleDeleteImage(currentBlankIndex)}
                    position="top-right-sm"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <AiFillPicture className="text-xl text-slate-400" />
                  <span className="text-[8px] text-slate-500 font-medium">Drop Image</span>
                </div>
              )}
            </DroppableZone>
          </span>
        );
      }

      return (
        <span key={`text-${index + 1}`} style={{ verticalAlign: "middle" }}>
          {part}
        </span>
      );
    });
  };

  return (
    <div
      ref={outerRef}
      style={{
        ...fillBlanksStyles.outerDiv,
        borderRadius: element.border.cornerRadius,
        backgroundColor: element.backgroundColor ?? "transparent",
        overflow: contentOverflowsFrame ? "visible" : fillBlanksStyles.outerDiv.overflow,
      }}
      className="text-black"
    >
      {isEditingText && !isLiveMode ? (
        // Edit mode: contentEditable with raw text
        <div
          contentEditable
          data-no-moveable-drag
          suppressContentEditableWarning
          ref={editableRef}
          onFocus={() => setIsEditingText(true)}
          onBlur={(e) => {
            handleQuestionTextChange(e.currentTarget.innerText);
            setIsEditingText(false);
          }}
          className="border-slate-200 bg-slate-50 hover:border-primary-300 hover:bg-white"
          style={{
            ...fillBlanksStyles.textContainer,
            fontSize: fillBlanksStyles.questionFontSize,
            fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
          }}
        >
          {element.text}
        </div>
      ) : (
        // Preview mode: parsed segments with droppable zones or numbered circles
        <div
          onClick={() => !isLiveMode && setIsEditingText(true)}
          data-no-moveable-drag
          className="border-slate-200 bg-slate-50 hover:border-primary-300 hover:bg-white"
          style={{
            ...fillBlanksStyles.textContainer,
            fontSize: fillBlanksStyles.questionFontSize,
            fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
            ...(!element.isTextOnly && { display: "flex", alignItems: "center", flexWrap: "wrap" }),
          }}
        >
          {renderTextWithBlanks()}
        </div>
      )}

      <div style={fillBlanksStyles.blocksContainer}>
        <div className="flex gap-2">
          {element.blocks?.map((_opt, index) => {
            const filled = answeredBlank(index);
            return (
              <button
                type="button"
                // biome-ignore lint/suspicious/noArrayIndexKey: only render number
                key={index}
                style={{
                  ...fillBlanksStyles.blankNumber,
                  ...(filled
                    ? {
                        backgroundColor: filled.isCorrect
                          ? answerColors.correct
                          : answerColors.wrong,
                      }
                    : {}),
                }}
                onClick={() => setCurrentIndex(index)}
                className={safeIndex === index ? "" : "opacity-40"}
              >
                {index + 1}
              </button>
            );
          })}
        </div>

        {element.answerMode === "type" ? (
          // Type mode: single text input bound to options[0] + submit button
          <div key={`blank-${safeIndex}-type`} className="flex items-center gap-2 w-full">
            <div
              contentEditable={!isLiveMode}
              data-no-moveable-drag
              suppressContentEditableWarning
              onMouseDown={(e) => {
                if (!isLiveMode) e.currentTarget.focus();
              }}
              // Unlike the question text, the typed answer must match a student's input exactly —
              // an embedded "\n" here would silently break that comparison. Enter is a no-op
              // rather than newline insertion, for the same reason a single-line <input> ignores it.
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
              onBlur={(e) => handleAnswerTextChange(safeIndex, 0, e.currentTarget.innerText)}
              className="flex-1 outline-none px-3 py-2 border-2 border-slate-300 rounded-lg bg-white focus:border-primary-400 text-center transition-colors"
              style={{
                fontSize: fillBlanksStyles.optionFontSize,
                fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
                ...gradedFillStyle(typedGrade),
              }}
            >
              {typedAnswer ?? element.blocks?.[safeIndex]?.options[0]?.text ?? ""}
            </div>
            {showTypedCorrectText ? (
              <span
                style={{
                  ...gradedFillStyle("correct"),
                  fontSize: fillBlanksStyles.optionFontSize,
                  fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
                  borderRadius: 8,
                  padding: "8px 12px",
                }}
              >
                {correctTypedText}
              </span>
            ) : (
              <button
                type="button"
                disabled={isLiveMode}
                className="flex items-center justify-center p-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <PiPaperPlaneTiltFill className="text-xl" />
              </button>
            )}
          </div>
        ) : (
          // Choice mode: render all options with correctness checkboxes
          element.blocks?.[safeIndex]?.options.map((option, optionIndex) => (
            <div
              key={`blank-${safeIndex}-option-${optionIndex + 1}`}
              style={{
                ...fillBlanksStyles.optionContainer,
                ...gradedFillStyle(
                  option.isCorrectAnswer
                    ? isBlankFrozen || isFrozen
                      ? "correct"
                      : undefined
                    : selectedAnswers?.[`${safeIndex}`] === `${optionIndex}` && isBlankFrozen
                      ? "wrong"
                      : undefined
                ),
              }}
            >
              {!isLiveMode &&
                (option.isCorrectAnswer ? (
                  <MdCheckBox
                    onClick={() => handleToggleCorrectAnswer(safeIndex, optionIndex)}
                    className="absolute left-2 text-success-500 text-xl cursor-pointer transition-transform hover:scale-110"
                  />
                ) : (
                  <MdCheckBoxOutlineBlank
                    onClick={() => handleToggleCorrectAnswer(safeIndex, optionIndex)}
                    className="absolute left-2 text-slate-400 text-xl cursor-pointer transition-transform hover:scale-110"
                  />
                ))}
              <div
                contentEditable={!isLiveMode}
                data-no-moveable-drag
                suppressContentEditableWarning
                onMouseDown={(e) => {
                  if (!isLiveMode) e.currentTarget.focus();
                }}
                // Same reasoning as the type-mode answer field above: an option's text is matched
                // against directly, so Enter is a no-op rather than inserting a newline.
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.preventDefault();
                }}
                onBlur={(e) =>
                  handleAnswerTextChange(safeIndex, optionIndex, e.currentTarget.innerText)
                }
                className="outline-none"
                style={{
                  fontSize: fillBlanksStyles.optionFontSize,
                  fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
                }}
              >
                {option.text}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
